// common
import { useEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import { doughnutColors, colorSetFor3d } from './ChartColors';

const GenericDonutChart = ({
  colorsList,
  data,
  dataCy,
  percentageInnerDonut,
  title,
  labelSuffix = '',
  displayDonutInnerText = true,
  unallocatedTransactionAmount,
  receivableAmtSum,
  innerRadiusPercent = 68,
  showCountOnly = false
}) => {

  const formattedDonutData = data?.datasets?.at(0)?.data;

  const colors = () => {
    if (colorsList?.length > 0) {
      return colorsList;
    } else {
      return colorSetFor3d.map((item) => item);
    }
  }

  useEffect(() => {

    const root = am5.Root.new(title);

    displayDoughnutChart(root);

    return () => {
      root.dispose();
    };
  }, [formattedDonutData]);

  const getBgColors = (index) => {
    if (colorsList?.length > 0) {
      return { backgroundColor: `${colorsList[index]}` }
    } else {
      return { backgroundColor: `${colorSetFor3d[index]}` }
    }
  }

  const convertToMillions = (inputAmount) => {
    if (inputAmount) {
      const inMillions = parseFloat(inputAmount / 1000000).toFixed(2);
      return `${inMillions} M`;
    }
    return inputAmount;
  };

  const CustomLegendLabels = () => {
    return (
      <div style={{ marginLeft: '20px', marginTop: '-40px' }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>
            Total Amt: <span style={{ fontWeight: 600 }}> {convertToMillions(receivableAmtSum)} </span>
          </span>
          <span>
            Total Unallocated Amt: <span style={{ fontWeight: 600 }}> {convertToMillions(unallocatedTransactionAmount)} </span>
          </span>
        </div>
        {formattedDonutData.map((item, index) =>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <span key={item.title} className="legendLabel ms-2 me-2" style={{ ...getBgColors(index), borderRadius: 3 }} />
            <span style={{ fontSize: "14px" }}>{item.title}</span>
          </div>
        )}
      </div>
    )
  }

  const displayDoughnutChart = (root) => {

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: root.horizontalLayout,
        centerX: am5.percent(0),
        centerY: am5.percent(1),
        radius: am5.percent(99),
        innerRadius: am5.percent(innerRadiusPercent),
        hoverOnFocus: true,
        isMeasered: true,
        autoMargins: false,
      })
    );

    // Create series
    let series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: 'amount',
        categoryField: 'title',
        alignLabels: true
      })
    );

    // slice colors
    series
      .get('colors')
      .set('colors', colors());

    // If we click on a slice in a pie series it will pull out of the place:disabled
    series.slices.template.set('toggleKey', 'none');

    // disables slice ticks
    series.ticks.template.setAll({ forceHidden: true });
    // disabled tick labels
    series.labels.template.setAll({ forceHidden: true });
    // Disable hovering magnification effect
    series.slices.template.states.create('hover', { scale: 1 });

    // Configure slices to be interactive - ensure tooltips work
    series.slices.template.setAll({
      focusable: true,
      stroke: am5.color(0xffffff),
      strokeWidth: 0,
      cursorOverStyle: "pointer"
    });

    if (displayDonutInnerText) {
      series.children.push(
        am5.Label.new(root, {
          text: percentageInnerDonut,
          inside: true,
          isMeasured: false,
          fontSize: 22,
          fontWeight: 700,
          paddingLeft: percentageInnerDonut.length >= 2 ? 2 : 1,
          fontFamily: 'Avenir, Arial, san-Serif',
          textBaseline: 'bottom',
          lineHeight: 0.5,
          textAlign: 'center',
          fill: '#003B5C',
          background: percentageInnerDonut.length > 0 ? am5.Circle.new(root, {
            shadowColor: am5.color('#0xd9d9d9'),
            fill: doughnutColors.Mosaic_White,
            shadowBlur: 10,
            shadowOffsetX: 2,
            shadowOffsetY: 10,
            scale: 3.8,
            opacity: 1,
          }) : null

        })

      );
    }

    // Display tooltip - Make it large and visible like the progress bar tooltip
    if (!data?.disableTooltips) {
      // Create tooltip with proper configuration - make it very visible
      let tooltip = am5.Tooltip.new(root, {
        autoTextColor: false,
        getFillFromSprite: false,
        getStrokeFromSprite: false,
        getLabelFillFromSprite: false,
        pointerOrientation: "horizontal",
        position: "pointer",
        dy: -25,
        layer: 100,
        layerMargin: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0
        },
        hoverOnFocus: true,
        forceInactive: false
      });

      // Customize tooltip text
      tooltip.label.adapters.add("text", function(text, target) {
        const dataItem = target.dataItem;
        if (!dataItem || !dataItem.dataContext) return text;
        
        const dataContext = dataItem.dataContext;
        
        if (dataContext.providers) {
          return getOtherProvidersList(dataContext.providers);
        }
        
        const title = dataContext.title || '';
        const count = dataContext.count ?? dataContext.amount ?? 0;
        const amount = dataContext.amount || 0;
        
        // Check if this is workflow status data
        const workflowStatusTitles = ['In Process', 'Completed', 'Rejected / Closed', 'Overdue', 'Rejected', 'Closed'];
        const isWorkflowStatus = showCountOnly || 
                                 workflowStatusTitles.includes(title) || 
                                 (count === amount && amount < 10000) || 
                                 (amount < 10000 && count > 0 && count === amount);
        
        if (isWorkflowStatus) {
          // Handle "Rejected / Closed" by splitting it into two lines
          if (title.includes(' / ')) {
            const parts = title.split(' / ');
            return `${parts[0]}\n${parts[1]}: ${count}`;
          }
          return `${title}: ${count}`;
        } else {
          return `${title} : ${count}${labelSuffix}\nAmount : ${convertToMillions(amount)}${labelSuffix}`;
        }
      });

      // Style the tooltip with black background like the progress bar tooltip - match In Progress bar style
      tooltip.get("background").setAll({
        fill: am5.color(0x000000),
        fillOpacity: 0.9,
        stroke: am5.color(0x000000),
        strokeWidth: 0,
        cornerRadiusTL: 4,
        cornerRadiusTR: 4,
        cornerRadiusBL: 4,
        cornerRadiusBR: 4
      });

      tooltip.label.setAll({
        fill: am5.color(0xffffff),
        fontSize: 12,
        fontWeight: "500",
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 10,
        paddingRight: 10,
        lineHeight: 1.6
      });

      // Make sure tooltip is visible
      tooltip.set("visible", true);
      
      // Set tooltip on series BEFORE setting data
      series.set("tooltip", tooltip);
    }

    function getOtherProvidersList(providersData) {

      return providersData.join('\n');

    }

    // Set data AFTER tooltip is configured
    series.data.setAll(formattedDonutData);

    // added doughnut inner text with styles
    root._logo.dispose();

  };



  return (
    <div style={{ display: "flex", justifyContent: 'space-between' }}>
      <div
        id={title}
        style={{ width: 300, height: 200, marginBottom: 10 }}
        data-cy={dataCy}
      />

      <CustomLegendLabels />
    </div>
  );
};

export default GenericDonutChart;
